import { Injectable } from '@angular/core';
import { Observable, Subscription, of } from 'rxjs';
import { catchError, defaultIfEmpty, mapTo } from 'rxjs/operators';
import { SessionStorageKey, SessionStorageState } from '../../../../../../../lib/helpers/session-storage.helper';
import { IdentityVerificationFacade } from '../../../../domain/+state/identity-verification.facade';
import { IdentityVerificationStatus, IdVerseFailedAction, IdVerseStateData } from '../../../../domain/models/identity-verification.model';
import { IdVersePollingService } from '../../../../infrastructure/id-verse-polling.service';
import {
  ID_VERSE_MOBILE_POLLING_INTERVAL_MS,
  ID_VERSE_MOBILE_POLLING_TIMEOUT_MS,
} from '../models/id-verse-content.data';
import { IdVerseContentConfig, IdVerseFlowType } from '../models/id-verse-content.model';
import { IdVerseContentStateService } from './id-verse-content-state.service';

@Injectable()
export class IdVerseMobileFlowService {
  private pollingSubscription: Subscription;

  constructor(
    private readonly identityVerificationFacade: IdentityVerificationFacade,
    private readonly idVersePollingService: IdVersePollingService,
    private readonly stateService: IdVerseContentStateService,
  ) { }

  handlePendingInitial(config: IdVerseContentConfig): Observable<boolean> {
    if (!this.consumeCompletedCallbackMarker()) {
      this.applyPendingFlow(config);
      return of(false);
    }

    return this.identityVerificationFacade.mobileFlowCompleted().pipe(
      mapTo(true),
      catchError(() => of(true)),
      defaultIfEmpty(true),
    );
  }

  handlePendingContinue(verificationUrl: string): void {
    SessionStorageState.remove(SessionStorageKey.AccountSetupViewed);
    SessionStorageState.set(SessionStorageKey.IdVerseReturnPending, true);
    this.navigateTo(verificationUrl);
  }

  handleSyncingStatus(): void {
    this.updateFlowType(IdVerseFlowType.MobileSyncing);
    this.restartPolling();
  }

  handleTryAgain(verificationUrl: string): void {
    SessionStorageState.remove(SessionStorageKey.AccountSetupViewed);
    SessionStorageState.set(SessionStorageKey.IdVerseReturnPending, true);
    this.navigateTo(verificationUrl);
  }

  stopPolling(): void {
    if (!this.pollingSubscription) {
      return;
    }

    this.pollingSubscription.unsubscribe();
    this.pollingSubscription = null;
  }

  private resolvePendingFlowType(config: IdVerseContentConfig): IdVerseFlowType {
    if (config?.showMobileAppPromo && !this.stateService.getSkipPromo()) {
      return IdVerseFlowType.MobileAppPromo;
    }

    return IdVerseFlowType.StandardPendingFlow;
  }

  private applyPendingFlow(config: IdVerseContentConfig): void {
    const flowType = this.resolvePendingFlowType(config);
    this.updateFlowType(flowType, flowType !== IdVerseFlowType.MobileAppPromo);
  }

  private restartPolling(): void {
    this.stopPolling();
    this.pollingSubscription = this.idVersePollingService
      .startPolling(ID_VERSE_MOBILE_POLLING_INTERVAL_MS, ID_VERSE_MOBILE_POLLING_TIMEOUT_MS)
      .subscribe((stateData: IdVerseStateData) => this.handlePollingState(stateData));
  }

  private handlePollingState(stateData: IdVerseStateData): void {
    if (!stateData) {
      return;
    }

    switch (stateData.status) {
      case IdentityVerificationStatus.Success:
        this.handleSuccessStatus();
        break;

      case IdentityVerificationStatus.Failed:
        this.handleFailedStatus(stateData.onFailedAction);
        break;
    }
  }

  private consumeCompletedCallbackMarker(): boolean {
    const hasCompletedCallbackMarker: boolean = SessionStorageState.get(SessionStorageKey.IdVerseCompletedCallback) === true;

    if (!hasCompletedCallbackMarker) {
      return false;
    }

    SessionStorageState.remove(SessionStorageKey.IdVerseCompletedCallback);
    return true;
  }

  private handleSuccessStatus(): void {
    this.stopPolling();
    this.updateFlowType(IdVerseFlowType.Success);
  }

  private handleFailedStatus(onFailedAction: IdVerseFailedAction): void {
    this.stopPolling();
    this.updateFlowType(IdVerseFlowType.Failed, true, onFailedAction);
  }

  private updateFlowType(flowType: IdVerseFlowType, skipMobileAppPromo: boolean = true, failedAction?: IdVerseFailedAction): void {
    this.stateService.update({
      currentFlowType: flowType,
      skipMobileAppPromo,
      failedAction: failedAction || null,
    });
  }

  protected navigateTo(url: string): void {
    window.location.assign(url);
  }
}